import { getSsoSession } from 'auth/ssoClient'
import type { BriefSources } from './buildBrief'
import { cfg } from 'config/runtimeConfig'
import { rememberRejectedToken } from './aiInsights'

/**
 * Customer Memory, the screen's side: read a record's memory, and let a PERSON act on one item.
 *
 * All of it goes to the editions service (REACT_APP_EDITIONS_API_URL) - the only thing that touches
 * the memory tables. The AI never writes through here: its updates are made server to server, after
 * the wallet has been settled. The one exception is `savePending`, for the rare case where the AI
 * read was paid for but its update could not be stored; the operations it posts were already
 * verified by the AI service, and the editions service verifies the caller and the items again.
 *
 * With no editions URL configured the feature is simply not offered - no chip, no calls.
 */

const EDITIONS_ORIGIN: string = (cfg('REACT_APP_EDITIONS_API_URL') ?? '').trim().replace(/\/+$/, '')

export const MEMORY_ENABLED: boolean = EDITIONS_ORIGIN !== ''

export type MemoryKind = 'want' | 'question' | 'promise' | 'objection' | 'fact'
export type MemoryStatus = 'open' | 'done' | 'dropped' | 'superseded'

export interface MemoryItem {
  id: number
  kind: MemoryKind
  text: string
  status: MemoryStatus
  /** yyyy-MM-dd - promises only. */
  dueOn: string | null
  ownerUserId: number | null
  sourceChannel: string | null
  /** The account's wall clock, as the history gives it. */
  sourceAt: string | null
  sourceQuote: string | null
  origin: 'ai' | 'person'
  /** null = the AI proposed it and nobody has confirmed it yet. */
  confirmedBy: number | null
  closedAt: string | null
  closedNote: string | null
  /** true when the AI closed it from the history, not a person. */
  closedByAi: boolean
  createdAt: string | null
}

export interface RecordMemory {
  summary: string | null
  /** Newest history entry an AI read has covered; sent back so the next read knows where it left off. */
  readUpTo: string | null
  items: MemoryItem[]
}

/** One verified operation, exactly as the AI service hands it back when its update was not stored. */
export interface PendingOp {
  op: string
  itemId?: number
  kind?: string
  text?: string
  status?: string
  note?: string
  dueOn?: string
  sourceChannel?: string
  sourceAt?: string
  sourceQuote?: string
}

export class MemoryError extends Error {
  constructor(message: string, readonly signedOut = false) {
    super(message)
  }
}

type Row = Record<string, unknown>

const text = (v: unknown): string | null => (typeof v === 'string' && v.trim() !== '' ? v : null)
const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null)

function toItem(row: Row): MemoryItem {
  return {
    id: Number(row.item_id),
    kind: String(row.kind) as MemoryKind,
    text: String(row.item_text ?? ''),
    status: String(row.status) as MemoryStatus,
    dueOn: text(row.due_on)?.slice(0, 10) ?? null,
    ownerUserId: num(row.owner_user_id),
    sourceChannel: text(row.source_channel),
    sourceAt: text(row.source_at),
    sourceQuote: text(row.source_quote),
    origin: row.origin === 'person' ? 'person' : 'ai',
    confirmedBy: num(row.confirmed_by),
    closedAt: text(row.closed_at),
    closedNote: text(row.closed_note),
    closedByAi: num(row.closed_by) === null && num(row.closed_run_id) !== null,
    createdAt: text(row.created_at),
  }
}

function identity(): { userId: number; parentId: number; token: string } {
  const session = getSsoSession()
  if (!session.token || !session.userId || !session.parentId) throw new MemoryError('You are not signed in.', true)
  return { userId: session.userId, parentId: session.parentId, token: session.token }
}

async function call<T>(path: string, init: RequestInit & { token: string }, signal?: AbortSignal): Promise<T> {
  let response: Response
  try {
    response = await fetch(`${EDITIONS_ORIGIN}${path}`, {
      ...init,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${init.token}` },
      signal,
    })
  } catch (err) {
    if ((err as Error).name === 'AbortError') throw err
    throw new MemoryError('The memory service could not be reached.')
  }
  if (response.status === 401) {
    rememberRejectedToken()
    throw new MemoryError('Your sign-in has timed out.', true)
  }
  if (!response.ok) {
    // the service answers { error: "..." } in plain words - show them rather than a status code
    const body = (await response.json().catch(() => null)) as { error?: string } | null
    throw new MemoryError(body?.error ?? `The memory service answered with an error (${response.status}).`)
  }
  return (await response.json()) as T
}

export async function fetchMemory(entityType: 'lead' | 'enquiry', entityId: string, signal?: AbortSignal): Promise<RecordMemory> {
  const me = identity()
  const query = new URLSearchParams({ userId: String(me.userId), parentId: String(me.parentId), entityType, entityId })
  const data = await call<{ memory: Row | null; items: Row[] }>(`/api/ed/cm?${query}`, { method: 'GET', token: me.token }, signal)
  return {
    summary: text(data.memory?.summary),
    readUpTo: text(data.memory?.read_up_to),
    items: (data.items ?? []).map(toItem).filter((item) => Number.isFinite(item.id)),
  }
}

export type ItemAction =
  | { action: 'add'; kind: MemoryKind; text: string; dueOn?: string }
  | { action: 'confirm'; itemId: number }
  | { action: 'edit'; itemId: number; text: string; dueOn?: string }
  | { action: 'close'; itemId: number; status: 'done' | 'dropped'; note?: string }
  | { action: 'reopen'; itemId: number }

export async function setMemoryItem(entityType: 'lead' | 'enquiry', entityId: string, change: ItemAction): Promise<void> {
  const me = identity()
  await call('/api/ed/cm/item', {
    method: 'POST',
    token: me.token,
    body: JSON.stringify({ userId: me.userId, parentId: me.parentId, entityType, entityId: Number(entityId), ...change }),
  })
}

/** The AI read was paid for but its update was not stored: store it now. Safe to repeat. */
export async function savePending(
  entityType: 'lead' | 'enquiry',
  entityId: string,
  ops: PendingOp[],
  summary: string | null,
  readTo: string | null,
): Promise<void> {
  const me = identity()
  await call('/api/ed/cm/apply', {
    method: 'POST',
    token: me.token,
    body: JSON.stringify({ userId: me.userId, parentId: me.parentId, entityType, entityId: Number(entityId), summary, readTo, ops, trigger: 'manual' }),
  })
}

// ---- the history as last read (sql/330) --------------------------------------------------------

export interface BriefSnapshot {
  sources: BriefSources
  readUpTo: string | null
  unavailable: string[]
  /** UTC, when the rows were stored. */
  computedAt: Date
}

/** The rows the last open read, or null when this record has never been opened with Insights. */
export async function fetchSnapshot(entityType: 'lead' | 'enquiry', entityId: string, signal?: AbortSignal): Promise<BriefSnapshot | null> {
  const me = identity()
  const query = new URLSearchParams({ userId: String(me.userId), parentId: String(me.parentId), entityType, entityId })
  const data = await call<{ snapshot: Row | null }>(`/api/ed/cm/snapshot?${query}`, { method: 'GET', token: me.token }, signal)
  const row = data.snapshot
  if (!row || typeof row.sources_json !== 'string') return null
  let sources: BriefSources
  try {
    sources = JSON.parse(row.sources_json) as BriefSources
  } catch {
    return null // an unreadable snapshot is no snapshot; the live read replaces it
  }
  const at = typeof row.computed_at === 'string' ? new Date(row.computed_at) : null
  if (!at || Number.isNaN(at.getTime())) return null
  return {
    sources,
    readUpTo: text(row.read_up_to),
    unavailable: text(row.unavailable)?.split(',').map((c) => c.trim()).filter(Boolean) ?? [],
    computedAt: at,
  }
}

/** Store what was just read. Best effort: a failure here costs nothing but the next open's wait. */
export async function putSnapshot(
  entityType: 'lead' | 'enquiry',
  entityId: string,
  sources: BriefSources,
  readUpTo: string | null,
  entryCount: number,
  unavailable: string[],
): Promise<void> {
  const me = identity()
  const { nameHints: _omit, ...rows } = sources // names come from the record itself, not from the snapshot
  await call('/api/ed/cm/snapshot', {
    method: 'POST',
    token: me.token,
    body: JSON.stringify({
      userId: me.userId, parentId: me.parentId, entityType, entityId: Number(entityId),
      sourcesJson: JSON.stringify(rows), readUpTo, entryCount, unavailable: unavailable.join(','),
    }),
  })
}
